// <s-closet-rack> — interactive dress-up closet. Each [data-closet-item] hangs a product;
// hovering (CSS) or tapping its [data-closet-garment] reveals the photo and a tag with sizes.
// [data-closet-add] adds the selected size to the cart, and garments can be dragged onto
// [data-closet-bag]. Cart writes go through Shopify Standard Actions when available, falling
// back to the AJAX cart at data-cart-add-url, then refresh mounted <s-cart> partials.
// Motion (fly-to-bag, bag bounce) is skipped when the buyer prefers reduced motion.
class SClosetRack extends HTMLElement {
  #ac = null;
  #reducedMotion = null;
  #dragItem = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('click', (event) => this.#onClick(event), opts);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeAll();
    }, opts);
    this.addEventListener('dragstart', (event) => this.#onDragStart(event), opts);
    this.addEventListener('dragend', () => this.#onDragEnd(), opts);

    const bag = this.#bag();
    if (bag) {
      bag.addEventListener('dragover', (event) => {
        if (!this.#dragItem) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        bag.classList.add('is-drop-target');
      }, opts);
      bag.addEventListener('dragleave', () => bag.classList.remove('is-drop-target'), opts);
      bag.addEventListener('drop', (event) => {
        event.preventDefault();
        bag.classList.remove('is-drop-target');
        const item = this.#dragItem;
        this.#dragItem = null;
        if (item) this.#add(item);
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('[data-closet-add]');
    if (addButton) {
      const item = addButton.closest('[data-closet-item]');
      if (item) this.#add(item);
      return;
    }

    const garment = target.closest('[data-closet-garment]');
    if (garment) {
      const item = garment.closest('[data-closet-item]');
      const willOpen = !item.classList.contains('is-open');
      this.#closeAll();
      if (willOpen) {
        item.classList.add('is-open');
        garment.setAttribute('aria-expanded', 'true');
      }
    }
  }

  #closeAll() {
    for (const item of this.querySelectorAll('[data-closet-item].is-open')) {
      item.classList.remove('is-open');
      item.querySelector('[data-closet-garment]')?.setAttribute('aria-expanded', 'false');
    }
  }

  #onDragStart(event) {
    const garment = event.target instanceof Element ? event.target.closest('[data-closet-garment]') : null;
    if (!garment) return;
    this.#dragItem = garment.closest('[data-closet-item]');
    this.#dragItem?.classList.add('is-dragging');
    this.classList.add('is-dragging');
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('text/plain', this.#dragItem?.dataset.productTitle || '');
    const photo = garment.querySelector('[data-closet-photo]');
    if (photo) event.dataTransfer.setDragImage(photo, photo.clientWidth / 2, photo.clientHeight / 3);
  }

  #onDragEnd() {
    this.#dragItem?.classList.remove('is-dragging');
    this.classList.remove('is-dragging');
    this.#bag()?.classList.remove('is-drop-target');
    // Keep #dragItem until drop has run; drop fires before dragend.
    this.#dragItem = null;
  }

  #bag() {
    return this.querySelector('[data-closet-bag]');
  }

  #selectedVariantId(item) {
    const checked = item.querySelector('input[data-closet-size]:checked');
    if (checked instanceof HTMLInputElement) return checked.value;
    return item.dataset.variantId || '';
  }

  async #add(item) {
    if (item.dataset.adding === 'true') return;
    const variantId = this.#selectedVariantId(item);
    if (!variantId) return;

    const button = item.querySelector('[data-closet-add]');
    const label = button?.querySelector('[data-closet-add-label]');
    const status = item.querySelector('[data-closet-status]');
    item.dataset.adding = 'true';
    if (label) label.textContent = this.dataset.addingLabel || '';
    if (status) status.textContent = '';
    button?.setAttribute('aria-disabled', 'true');

    try {
      await this.#addToCart(variantId);
      if (label) label.textContent = this.dataset.addedLabel || '';
      this.#celebrate(item);
      setTimeout(() => {
        if (label) label.textContent = this.dataset.addLabel || '';
      }, 1600);
    } catch (error) {
      if (label) label.textContent = this.dataset.addLabel || '';
      if (status) status.textContent = (typeof error?.message === 'string' && error.message) || this.dataset.errorLabel || '';
    } finally {
      delete item.dataset.adding;
      button?.removeAttribute('aria-disabled');
    }
  }

  async #addToCart(variantId) {
    const line = { merchandiseId: variantId, quantity: 1 };

    if (window.Shopify?.actions?.updateCart) {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        { event: { context: 'closet', detail: { source: 's-closet-rack' } } }
      );
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError);
      return;
    }

    const addUrl = this.dataset.cartAddUrl;
    if (!addUrl) throw new Error('');
    const response = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.description || '');
    }
    await customElements.get('s-cart')?.refreshMountedCarts?.();
  }

  #celebrate(item) {
    const bag = this.#bag();
    const count = this.querySelector('[data-closet-count]');
    if (count) {
      const next = (parseInt(count.textContent, 10) || 0) + 1;
      count.textContent = String(next);
      count.hidden = false;
    }
    if (!bag || this.#reducedMotion?.matches) return;

    bag.animate(
      [
        { transform: 'scale(1) rotate(0deg)' },
        { transform: 'scale(1.18) rotate(-6deg)' },
        { transform: 'scale(0.96) rotate(4deg)' },
        { transform: 'scale(1) rotate(0deg)' },
      ],
      { duration: 520, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );

    const photo = item.querySelector('[data-closet-photo]');
    if (!(photo instanceof HTMLImageElement)) return;
    const from = photo.getBoundingClientRect();
    const to = bag.getBoundingClientRect();
    if (!from.width || !to.width) return;

    const ghost = photo.cloneNode(true);
    ghost.removeAttribute('srcset');
    ghost.src = photo.currentSrc || photo.src;
    ghost.setAttribute('aria-hidden', 'true');
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      opacity: '1',
      pointerEvents: 'none',
      zIndex: '60',
      margin: '0',
    });
    document.body.append(ghost);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    ghost
      .animate(
        [
          { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
          { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 80}px) scale(0.55) rotate(-12deg)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.08) rotate(8deg)`, opacity: 0.2 },
        ],
        { duration: 650, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
      )
      .finished.finally(() => ghost.remove());
  }
}

if (!customElements.get('s-closet-rack')) customElements.define('s-closet-rack', SClosetRack);
